/**
 * Utility functions for filter operations
 */

import type { KnowledgeBaseFilter } from "_graphql-types/graphql";
import {
  FILTER_REGISTRY,
  getFilterByStoreKey,
  type FilterConfig,
} from "../config/filterRegistry";
import type { ActiveFilters, FilterValue } from "../stores/knowledgeBaseStore";

/**
 * Convert GraphQL KnowledgeBaseFilter to frontend ActiveFilters.
 * Iterates each filter registry entry's fromGraphQL() converter.
 */
export function fromGraphQLToActiveFilters(
  filters: Partial<KnowledgeBaseFilter>
): ActiveFilters {
  let restoredFilters: Partial<ActiveFilters> = {};
  Object.values(FILTER_REGISTRY).forEach(config => {
    if (typeof config.fromGraphQL === "function") {
      const partial = config.fromGraphQL(filters);
      if (partial) {
        restoredFilters = {
          ...restoredFilters,
          ...partial,
        };
      }
    }
  });
  return restoredFilters as ActiveFilters;
}

/**
 * Get display label for a filter from active filters
 */
export function getFilterDisplayLabel(
  storeKey: string,
  activeFilters: ActiveFilters
): string | null {
  const config = getFilterByStoreKey(storeKey);
  if (!config) return null;

  try {
    return config.getDisplayLabel(activeFilters);
  } catch (error) {
    console.error(`Error getting display label for ${storeKey}:`, error);
    return null;
  }
}

/**
 * Get summary of all active filters
 */
export function getFilterSummary(filters: ActiveFilters): string {
  const parts: string[] = [];
  const processedFilterIds = new Set<string>(); // Track which filters we've already added

  Object.keys(filters).forEach(storeKey => {
    const config = getFilterByStoreKey(storeKey);
    if (!config) return;

    // Skip if we've already processed this filter (for multi-key filters like date ranges)
    if (processedFilterIds.has(config.id)) return;
    processedFilterIds.add(config.id);

    const label = getFilterDisplayLabel(storeKey, filters);
    if (label) {
      parts.push(label);
    }
  });

  return parts.length > 0 ? parts.join(", ") : "No filters applied";
}

/**
 * Group active filters by category for display
 */
export interface GroupedFilter {
  config: FilterConfig;
  label: string;
  value: any;
}

export function groupFiltersByCategory(
  activeFilters: ActiveFilters
): Record<string, GroupedFilter[]> {
  const grouped: Record<string, GroupedFilter[]> = {};
  const processedFilterIds = new Set<string>(); // Track which filters we've already added

  Object.entries(activeFilters).forEach(([storeKey, value]) => {
    const config = getFilterByStoreKey(storeKey);
    if (!config || !value) return;

    // Skip if we've already processed this filter (for multi-key filters like date ranges)
    if (processedFilterIds.has(config.id)) return;
    processedFilterIds.add(config.id);

    const label = config.getDisplayLabel(activeFilters);
    if (!label) return;

    const category = config.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }

    grouped[category].push({
      config,
      label,
      value,
    });
  });

  return grouped;
}

/**
 * Remove a filter from active filters
 * Handles both single keys and multi-key filters (like date ranges)
 */
export function removeFilter(
  activeFilters: ActiveFilters,
  filterIdOrStoreKey: string,
  specificValue?: any
): ActiveFilters {
  const newFilters = { ...activeFilters };

  // Try to find by filter ID first
  const config = Object.values(FILTER_REGISTRY).find(
    c => c.id === filterIdOrStoreKey
  );

  if (config) {
    // Remove all store keys for this filter
    const keys = Array.isArray(config.storeKeys)
      ? config.storeKeys
      : [config.storeKeys];
    keys.forEach(key => {
      delete newFilters[key as keyof ActiveFilters];
    });
  } else {
    // Fallback: remove by store key directly
    const filterKey = filterIdOrStoreKey as keyof ActiveFilters;
    if (specificValue !== undefined && Array.isArray(newFilters[filterKey])) {
      // Remove specific value from array
      const filtered = (
        newFilters[filterKey] as Array<FilterValue | string | number>
      ).filter((v: FilterValue | string | number) => {
        if (typeof v === "object" && v !== null && "id" in v) {
          return (v as FilterValue).id !== specificValue.id;
        }
        return v !== specificValue;
      });

      if (filtered.length === 0) {
        // Remove the entire key
        const { [filterKey]: _, ...rest } = newFilters;
        return rest;
      } else {
        // Return new object with filtered array
        return { ...newFilters, [filterKey]: filtered } as ActiveFilters;
      }
    } else {
      // Remove entire filter
      const { [filterKey]: _, ...rest } = newFilters;
      return rest;
    }
  }

  return newFilters;
}

/**
 * Clear all filters
 */
export function clearAllFilters(): ActiveFilters {
  return {};
}

const STATUS_TEXT_MAP: Record<string, string> = {
  READY: "ready",
  MATERIALIZING: "loading",
  ERROR: "error",
  PENDING: "pending",
};

/**
 * Get display text and MUI color for a knowledge base status.
 */
export function getKnowledgeBaseStatus(status: string): {
  text: string;
  color: string;
} {
  const text = STATUS_TEXT_MAP[status] ?? status?.toLowerCase() ?? "unknown";
  const color =
    status === "READY"
      ? "success.main"
      : status === "MATERIALIZING"
        ? "info.main"
        : status === "ERROR"
          ? "error.main"
          : "text.secondary";
  return { text, color };
}
